import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { GradeReference, PipeReference, WellComponent } from 'src/app/wellbore-inputs/components/well-configuration/models';
import { CustomConnectionModel } from '../models/custom-connection.model';
import { TubularString } from 'src/app/wellbore-inputs/models/well-schematic.model';
import { Fluid } from 'src/app/perical/models/fluid.model';
import { AnnulusContent } from 'src/app/wellbore-inputs/components/well-configuration/models/annulus-contents';
import { Riser } from 'src/app/wellbore-inputs/components/riser-component/riser.model';
import { ESP, ESPs } from 'src/app/wellbore-inputs/models/esp.model';

const PeriforApiEndpoint = `${environment.PeriforEndpoint}/api`;

@Injectable({
  providedIn: 'root'
})
export class WellConfigService {
  private annulusContentsCache$: Record<string, Observable<AnnulusContent>> = {};
  private pipeCatalogCache$: Observable<PipeReference[]> = null;
  private gradeCatalogCache$: Observable<GradeReference[]> = null;
  private tubularCache$: Observable<TubularString> = null;
  // Have to do this by designId because the lookup data can vary by design
  // e.g., when opening a new design, you don't want cached tubulars from the last design.
  private stringsListCache$: Record<string, Observable<any>> = {};
  private connectionsListCache$: Observable<CustomConnectionModel[]> = null;
  private pipeOdsCache$: Observable<number[]> = null;
  private fluidsListCache$: Observable<Fluid[]> | null = null;
  private riserCache$: Observable<Riser> | null = null;
    private espCache$: Record<string, Observable<ESPs>> = {};

  constructor(private http: HttpClient) { }

  public getTubulars(): Observable<WellComponent[]> {
    return this.http.get<WellComponent[]>(`${PeriforApiEndpoint}/WellConfiguration/tubulars`).pipe(catchError(e => of(e)));
  }

  public getPipeCatalog(): Observable<PipeReference[]> {
    if (!this.pipeCatalogCache$) {
      this.pipeCatalogCache$ = this.http
        .get<PipeReference[]>(`${PeriforApiEndpoint}/pipesCatalog`)
        .pipe(
          catchError(() => of([])),
          shareReplay(1)
        );
    }
    setTimeout(() => this.pipeCatalogCache$ = null, 1000);
    return this.pipeCatalogCache$;
  }

  public getPipeCatalogByOd(selectedOd: number): Observable<PipeReference[]> {
    return this.http.get<PipeReference[]>(`${PeriforApiEndpoint}/pipesCatalog?od=${selectedOd}`).pipe(catchError(e => of(e)));
  }

  public getPipeOds(): Observable<number[]> {
    if (!this.pipeOdsCache$) {
      this.pipeOdsCache$ = this.http
        .get<number[]>(`${PeriforApiEndpoint}/pipesCatalog/ods`)
        .pipe(
          catchError(() => of([])),
          shareReplay(1)
        );
    }
    setTimeout(() => this.pipeOdsCache$ = null, 1000);
    return this.pipeOdsCache$;
  }

  public getGradeCatalog(): Observable<GradeReference[]> {
    if (!this.gradeCatalogCache$) {
      this.gradeCatalogCache$ = this.http
        .get<GradeReference[]>(`${PeriforApiEndpoint}/gradesCatalog`)
        .pipe(
          catchError(() => of([])),
          shareReplay(1)
        );
    }
    setTimeout(() => this.gradeCatalogCache$ = null, 1000);
    return this.gradeCatalogCache$;
  }

  public createNewTubular(tubular: WellComponent): Observable<string> { //Returns guid
    return this.http.post<string>(`${PeriforApiEndpoint}/WellConfiguration/tubulars`, tubular);
  }

  public editTubular(tubular: WellComponent): Observable<boolean> {
    return this.http.put<boolean>(`${PeriforApiEndpoint}/WellConfiguration/tubulars/${tubular.id}`, tubular)
  }

  public deleteTubular(tubularId: string): Observable<boolean> {
    return this.http.delete<boolean>(`${PeriforApiEndpoint}/WellConfiguration/tubulars/${tubularId}`)
  }

  public getStringsList(designId?: string): Observable<any> {
    const cacheKey = designId || 'default';
    
    if (!this.stringsListCache$[cacheKey]) {
      this.stringsListCache$[cacheKey] = this.http
        .get(`${PeriforApiEndpoint}/WellConfiguration/tubulars/lookup`)
        .pipe(
          catchError(() => of([])),
          shareReplay(1)
        );
      setTimeout(() => delete this.stringsListCache$[cacheKey], 1000);
    }
    
    return this.stringsListCache$[cacheKey];
  }

  public getConnectionsList(): Observable<CustomConnectionModel[]> {
    if (!this.connectionsListCache$) {
      this.connectionsListCache$ = this.http
        .get<CustomConnectionModel[]>(`${PeriforApiEndpoint}/customConnections`)
        .pipe(
          catchError(() => of([])),
          shareReplay(1)
        );
    }
    setTimeout(() => this.connectionsListCache$ = null, 1000);
    return this.connectionsListCache$;
  }

  public getTubular(): Observable<TubularString> {
    if (!this.tubularCache$) {
      this.tubularCache$ = this.http
        .get<TubularString>(`${PeriforApiEndpoint}/WellConfiguration/tubular`)
        .pipe(
          catchError(() => of(null as TubularString)),
          shareReplay(1)
        );
    }
    setTimeout(() => this.tubularCache$ = null, 1000);
    return this.tubularCache$;
  }

  public invalidateSelectedTubularData(): void {
    this.tubularCache$ = null;
    this.annulusContentsCache$ = {};
  }

  public getFluidsList(): Observable<Fluid[]> {
    if (!this.fluidsListCache$) {
      this.fluidsListCache$ = this.http
        .get<Fluid[]>(`${PeriforApiEndpoint}/fluids`)
        .pipe(
          catchError(() => of([])),
          shareReplay(1)
        );
    }
    setTimeout(() => this.fluidsListCache$ = null, 1000);
    return this.fluidsListCache$;
  }

public getAnnulusContents(tubularId: string | null): Observable<any> {
  const cacheKey = tubularId || 'no_tubular_id';

  if (!this.annulusContentsCache$) {
    this.annulusContentsCache$ = {};
  }

  if (!this.annulusContentsCache$[cacheKey]) {
    let params = new HttpParams();
    if (tubularId) {
      params = params.set('tubularId', tubularId);
    }

    this.annulusContentsCache$[cacheKey] = this.http
      .get<any>(`${PeriforApiEndpoint}/WellConfiguration/annulus-contents`, { params })
      .pipe(
        catchError(() => of(null as any)),
        shareReplay(1)
      );
  }

  setTimeout(() => {
    if (this.annulusContentsCache$) {
      this.annulusContentsCache$[cacheKey] = null;
    }
  }, 1000);

  return this.annulusContentsCache$[cacheKey];
}


  updateAnnulusContents(annulusContents: any, tubularId: string | null) {
    this.annulusContentsCache$ = null;

    let params = new HttpParams();
    if (tubularId) {
      params = params.set('tubularId', tubularId);
    }
    return this.http.put<AnnulusContent>(`${PeriforApiEndpoint}/WellConfiguration/annulus-contents`, annulusContents,
      {
        params: params
      }
    );
  }

  // Risers

  public getRiserForTubular(): Observable<Riser> {
    if (!this.riserCache$) {
      this.riserCache$ = this.http
        .get<Riser>(`${PeriforApiEndpoint}/WellConfiguration/riser`)
        .pipe(
          map(data => new Riser(data)),
          catchError(() => of(null as Riser)),
          shareReplay(1)
        );
    }
    setTimeout(() => this.riserCache$ = null, 1000);
    return this.riserCache$;
  }

  updateRiserForTubular(model: Riser): Observable<boolean> {
    return this.http.put<boolean>(`${PeriforApiEndpoint}/WellConfiguration/riser`, model);
  }

  addRiser(riser: Riser): Observable<Riser> {
    return this.http.post<Riser>(`${PeriforApiEndpoint}/WellConfiguration/riser`, riser);
  }

  // ESPs

  public getEspsForTubular(tubularId?: string | null): Observable<ESPs> {
    const cacheKey = tubularId || 'no_tubular_id';
    if (!this.espCache$[cacheKey]) {
      this.espCache$[cacheKey] = this.http
        .get<ESPs>(`${PeriforApiEndpoint}/WellConfiguration/esp`, { params: tubularId ? { tubularId } : {} })
        .pipe(
          catchError(() => of(null as ESPs)),
          shareReplay(1)
        );
    }
    setTimeout(() => this.espCache$[cacheKey] = null, 1000);
    return this.espCache$[cacheKey];
  }

  updateEsps(esps: ESP[]): Observable<boolean> {
    return this.http.put<boolean>(
      `${PeriforApiEndpoint}/WellConfiguration/esp`,
      esps
    );
  }
}
